import * as NextSkyTheme from "@NextSkyTheme/global";

let syncing = false;
let removingBundle = false;

function getRoot() {
  return window.Shopify?.routes?.root || "/";
}

async function fetchCart() {
  const response = await fetch(`${getRoot().replace(/\/?$/, "/")}cart.js`);
  if (!response.ok) throw new Error("Failed to load cart");
  return response.json();
}

async function clearByobCartAttributeIfEmpty() {
  try {
    const cart = await fetchCart();
    if (cart.items?.some(isByobShell)) return;
    await fetch(`${getRoot().replace(/\/?$/, "/")}cart/update.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        attributes: {
          byob_active: "",
          byob_validation: "",
        },
      }),
    });
  } catch (error) {
    console.warn("Failed to clear BYOB cart attribute", error);
  }
}

function isByobShell(item) {
  const props = item.properties || {};
  return props._byob_role === "shell" || Boolean(props._byob_variant_ids || props._byob_box_key);
}

function isByobGift(item) {
  return (item.properties || {})._byob_role === "gift";
}

function getBundleId(item) {
  return String((item.properties || {})._byob_bundle_id || "").trim();
}

function getCartDrawer() {
  return document.querySelector("cart-drawer");
}

function getMainCart() {
  return document.querySelector("main-cart");
}

function getSectionNames() {
  const drawer = getCartDrawer();
  if (drawer?.getSectionsToRender) {
    return drawer.getSectionsToRender().map((section) => section.section);
  }

  const mainCart = getMainCart();
  const sectionId = mainCart?.getAttribute("data-section-id");
  if (sectionId) {
    return [sectionId, "cart-icon-bubble"];
  }

  return ["cart-icon-bubble"];
}

function getRemoveComponent(button) {
  return button.closest("cart-remove-button, main-cart-remove-button");
}

async function collectBundleRemoveItems(lineKey) {
  const cart = await fetchCart();
  const shell = cart.items.find((item) => item.key === lineKey);
  if (!shell) return [];

  const bundleId = getBundleId(shell);
  const items = [{ key: lineKey }];

  if (bundleId) {
    cart.items.forEach((item) => {
      if (isByobGift(item) && getBundleId(item) === bundleId) {
        items.push({ key: item.key });
      }
    });
  }

  const seen = new Set();
  return items.filter((item) => {
    if (!item.key || seen.has(item.key)) return false;
    seen.add(item.key);
    return true;
  });
}

function renderCartResponse(data) {
  if (!data?.sections) return false;

  const drawer = getCartDrawer();
  if (drawer?.renderContents) {
    drawer.renderContents(data);
    return true;
  }

  const mainCart = getMainCart();
  if (!mainCart) return false;

  const sectionId = mainCart.getAttribute("data-section-id");
  const itemsHtml = data.sections[sectionId];
  if (itemsHtml) {
    const container = document.querySelector(".main-cart-items");
    if (container) {
      const template = document.createElement("template");
      template.innerHTML = itemsHtml;
      const replacement = template.content.querySelector(".main-cart-items");
      if (replacement) {
        container.replaceWith(replacement);
      }
    }
  }

  const bubble = document.getElementById("cart-icon-bubble");
  if (bubble && data.sections["cart-icon-bubble"]) {
    const template = document.createElement("template");
    template.innerHTML = data.sections["cart-icon-bubble"];
    const replacement = template.content.querySelector(".shopify-section") || template.content.firstElementChild;
    if (replacement) {
      bubble.innerHTML = replacement.innerHTML;
    }
  }

  return true;
}

async function changeLineQuantity(lineKey, quantity, { sections = null } = {}) {
  const payload = {
    id: lineKey,
    quantity,
  };

  if (sections?.length) {
    payload.sections = sections;
    payload.sections_url = window.location.pathname;
  }

  const config = NextSkyTheme.fetchConfig("json");
  config.body = JSON.stringify(payload);
  const response = await fetch(window.routes.cart_change_url, config);
  return response.json();
}

async function removeBundleWithTheme(button, items) {
  const removeComponent = getRemoveComponent(button);
  if (!removeComponent?.updateQuantitiesByKey || items.length === 0) return false;

  const line = Number(button.dataset.index);
  const payload = items.map((item, index) => ({
    key: item.key,
    line: index === 0 && Number.isFinite(line) ? line : null,
  }));

  removeComponent.updateQuantitiesByKey(payload, 0);
  return true;
}

async function removeBundleWithFetch(items) {
  const sections = getSectionNames();
  let lastResponse = null;

  for (let index = 0; index < items.length; index += 1) {
    const isLast = index === items.length - 1;
    lastResponse = await changeLineQuantity(items[index].key, 0, {
      sections: isLast ? sections : null,
    });
    if (lastResponse?.status) return false;
  }

  if (lastResponse) {
    renderCartResponse(lastResponse);
    document.dispatchEvent(new CustomEvent("nextsky:cart:added"));
    if (NextSkyTheme?.publish && NextSkyTheme.PUB_SUB_EVENTS?.cartUpdate) {
      NextSkyTheme.publish(NextSkyTheme.PUB_SUB_EVENTS.cartUpdate, {
        source: "byob-gift-sync",
      });
    }
  }

  return true;
}

async function waitForBundleRemovalIdle() {
  let attempts = 0;

  while (removingBundle && attempts < 100) {
    await new Promise((resolve) => {
      window.setTimeout(resolve, 50);
    });
    attempts += 1;
  }

  return !removingBundle;
}

export async function removeByobBundleItems(
  lineKey,
  { render = true, button = null, allowMissing = false } = {},
) {
  if (!lineKey) return false;

  const idle = await waitForBundleRemovalIdle();
  if (!idle) return false;

  removingBundle = true;

  try {
    const items = await collectBundleRemoveItems(lineKey);
    if (!items.length) return allowMissing;

    if (render && button) {
      const removed = await removeBundleWithTheme(button, items);
      if (removed) {
        await clearByobCartAttributeIfEmpty();
        return true;
      }
    }

    if (!render) {
      for (const item of items) {
        const data = await changeLineQuantity(item.key, 0);
        if (data?.status) return false;
      }

      await clearByobCartAttributeIfEmpty();
      return true;
    }

    return removeBundleWithFetch(items).then(async (result) => {
      await clearByobCartAttributeIfEmpty();
      return result;
    });
  } catch (error) {
    console.error(error);
    return false;
  } finally {
    removingBundle = false;
  }
}

async function syncByobGifts() {
  if (syncing || removingBundle) return false;

  syncing = true;
  let changed = false;
  let lastResponse = null;

  try {
    const cart = await fetchCart();
    const items = cart.items || [];
    const shellQtyByBundle = new Map();

    items.filter(isByobShell).forEach((shell) => {
      const bundleId = getBundleId(shell);
      if (!bundleId) return;
      shellQtyByBundle.set(bundleId, (shellQtyByBundle.get(bundleId) || 0) + shell.quantity);
    });

    const giftsToUpdate = items.filter(isByobGift).filter((gift) => {
      const bundleId = getBundleId(gift);
      const expectedQty = bundleId ? shellQtyByBundle.get(bundleId) || 0 : 0;
      return expectedQty !== gift.quantity;
    });

    if (!giftsToUpdate.length) return false;

    const sections = getSectionNames();

    for (let index = 0; index < giftsToUpdate.length; index += 1) {
      const gift = giftsToUpdate[index];
      const bundleId = getBundleId(gift);
      const expectedQty = bundleId ? shellQtyByBundle.get(bundleId) || 0 : 0;
      const isLast = index === giftsToUpdate.length - 1;

      lastResponse = await changeLineQuantity(gift.key, expectedQty, {
        sections: isLast ? sections : null,
      });
      if (!lastResponse?.status) changed = true;
    }

    if (changed && lastResponse) {
      renderCartResponse(lastResponse);
      document.dispatchEvent(new CustomEvent("nextsky:cart:added"));
      if (NextSkyTheme?.publish && NextSkyTheme.PUB_SUB_EVENTS?.cartUpdate) {
        NextSkyTheme.publish(NextSkyTheme.PUB_SUB_EVENTS.cartUpdate, {
          source: "byob-gift-sync",
        });
      }
    }

    await clearByobCartAttributeIfEmpty();
  } catch (error) {
    console.error(error);
  } finally {
    syncing = false;
  }

  return changed;
}

function lockGiftQuantityControls() {
  document.querySelectorAll(".cart-item--byob-gift quantity-input").forEach((input) => {
    input.setAttribute("hidden", "");
  });
}

function scheduleSync() {
  window.requestAnimationFrame(() => {
    syncByobGifts()
      .then(() => {
        lockGiftQuantityControls();
      })
      .catch(() => {});
  });
}

function isShellRemoveButton(target) {
  const button = target.closest("cart-remove-button, main-cart-remove-button");
  if (!button) return null;
  if (button.closest(".cart-item--byob-gift")) return null;
  if (!button.closest(".cart-item--byob-shell")) return null;
  return button;
}

function isGiftRemoveButton(target) {
  const button = target.closest("cart-remove-button, main-cart-remove-button");
  if (!button) return null;
  if (!button.closest(".cart-item--byob-gift")) return null;
  return button;
}

async function handleShellRemoveClick(event) {
  const button = isShellRemoveButton(event.target);
  if (!button) return;

  const lineKey = button.dataset.key;
  if (!lineKey) return;

  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();

  await removeByobBundleItems(lineKey, { button });
}

function blockGiftRemoveClick(event) {
  const button = isGiftRemoveButton(event.target);
  if (!button) return;

  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();
}

async function guardCheckoutForOrphanGifts(event) {
  const trigger = event.target.closest(
    'button[name="checkout"], input[name="checkout"], a[href*="/checkout"]',
  );
  if (!trigger) return;

  let cart;
  try {
    cart = await fetchCart();
  } catch {
    return;
  }

  const shellBundles = new Set(
    cart.items.filter(isByobShell).map((item) => getBundleId(item)).filter(Boolean),
  );
  const orphanGift = cart.items.some(
    (item) => isByobGift(item) && !shellBundles.has(getBundleId(item)),
  );

  if (!orphanGift) return;

  event.preventDefault();
  event.stopImmediatePropagation();
  await syncByobGifts();
}

document.addEventListener("click", handleShellRemoveClick, true);
document.addEventListener("click", blockGiftRemoveClick, true);
document.addEventListener("click", guardCheckoutForOrphanGifts, true);

document.addEventListener("DOMContentLoaded", () => {
  lockGiftQuantityControls();
  scheduleSync();
});

document.addEventListener("nextsky:cart:added", scheduleSync);

if (NextSkyTheme?.subscribe && NextSkyTheme.PUB_SUB_EVENTS?.cartUpdate) {
  NextSkyTheme.subscribe(NextSkyTheme.PUB_SUB_EVENTS.cartUpdate, (event) => {
    if (event?.source === "byob-gift-sync") return;
    scheduleSync();
  });
}

window.byobRemoveBundleItems = removeByobBundleItems;
